import { memo, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { isValid, parseISO } from "date-fns";
import { displayAccountLabel } from "@common/helpers/accounting";
import { pluralizeWithCount } from "@common/helpers/string";
import { AuditLogDiff } from "@common/types/auditLog";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { RuleGroupType } from "@common/types/transactionRule";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import {
    formatBusinessDate,
    formatDateForTransaction,
    formatDateOnly,
} from "@helpers/date";
import { Amount } from "@design-system/Amount/Amount";
import { useEntitiesById } from "@hooks/useEntities";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { useAccounts } from "@hooks/useAccounts";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { HistoryCategory } from "@components/history/components/HistoryCategory";
import { HistoryClass } from "@components/history/components/HistoryClass";
import { EmptyValue } from "@design-system/EmptyValue/EmptyValue";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { DocumentName } from "@components/documents/DocumentName";
import { HistoryTransactionDetailsRow } from "../views/historyTransactionDetailsViewConfig";
import styles from "./HistoryTransactionsDetailsCells.module.scss";

const RULE_GROUP_TYPE_LABELS: Record<string, string> = {
    [RuleGroupType.TRANSACTION]: "Transaction",
    [RuleGroupType.ACCOUNTING]: "Accounting",
    [RuleGroupType.TRANSFER]: "Transfer",
    [RuleGroupType.SPLIT]: "Split",
};

const DATE_FIELDS = new Set([
    "date",
    "dueDate",
    "due",
    "paidAt",
    "uncollectableAt",
    "voidedAt",
]);

interface ValueCellProps {
    field: string;
    value: AuditLogDiff["before" | "after"];
    resource?: Record<string, unknown>;
    entityId?: number;
    orderDirection?: "up" | "down" | null;
}
const ValueCell = memo(
    ({ field, value, resource, entityId, orderDirection }: ValueCellProps) => {
        const categories = useCategoriesById();
        const categoryDisplay = useMemo(() => {
            if (field !== "categoryId" || typeof value.value !== "string") {
                return null;
            }
            return (
                <HistoryCategory
                    value={{
                        value: value.value,
                        valueLabel: categories[value.value]?.label,
                    }}
                    field={field}
                    resource={resource}
                    categories={categories}
                />
            );
        }, [value, field, resource, categories]);

        const isAccountField =
            field === "accountId" ||
            field === "accrualAccountId" ||
            field === "generalLedgerAccountId" ||
            field === "accountsPayableId" ||
            field === "accountsReceivableId";
        const { accountsById: cashAccountsById } = useAccounts({
            entityId: isAccountField ? entityId : undefined,
            includeDisabled: true,
            ledgerBasis: AccountingLedgerBasis.cash,
        });
        const { accountsById: accrualAccountsById } = useAccounts({
            entityId: isAccountField ? entityId : undefined,
            includeDisabled: true,
            ledgerBasis: AccountingLedgerBasis.accruals,
        });
        const showAccountNumbers = useShowAccountNumbers();

        const accountDisplay = useMemo(() => {
            if (!isAccountField || typeof value.value !== "string") {
                return null;
            }
            const account =
                accrualAccountsById[value.value] ??
                cashAccountsById[value.value];
            if (!account) {
                return null;
            }
            return displayAccountLabel(account, showAccountNumbers);
        }, [
            isAccountField,
            value.value,
            accrualAccountsById,
            cashAccountsById,
            showAccountNumbers,
        ]);

        const classDisplay = useMemo(() => {
            if (field !== "classIds") {
                return null;
            }
            return <HistoryClass value={value} field={field} />;
        }, [field, value]);

        const dateDisplay = useMemo(() => {
            if (typeof value.value !== "string") {
                return null;
            }
            if (field === "dateOnly") {
                return (
                    <BreakableText
                        text={formatDateOnly(value.value)}
                        showTooltip
                    />
                );
            }
            if (!DATE_FIELDS.has(field)) {
                return null;
            }
            const parsed = parseISO(value.value);
            if (!isValid(parsed)) {
                return null;
            }
            // Only the transaction `date` field uses the stored "PST midnight as
            // UTC" convention; due/paid dates follow the invoice/bill convention.
            const formatted =
                field === "date"
                    ? formatBusinessDate(parsed)
                    : formatDateForTransaction(parsed);
            return <BreakableText text={formatted} showTooltip />;
        }, [field, value.value]);

        const allEntities = useEntitiesById();
        const entity = useMemo(
            () =>
                field === "entityId" && value.value !== null
                    ? allEntities.get(Number(value.value))
                    : undefined,
            [field, value.value, allEntities],
        );

        const displayValue = useMemo(() => {
            if (categoryDisplay) {
                return categoryDisplay;
            }
            if (classDisplay) {
                return classDisplay;
            }
            if (dateDisplay) {
                return dateDisplay;
            }
            if (field === "amount" && typeof value.value === "number") {
                return (
                    <Amount
                        amount={value.value}
                        showNegative
                        separateCurrencySign
                    />
                );
            }
            if (accountDisplay) {
                return <BreakableText text={accountDisplay} showTooltip />;
            }
            if (
                field === "financialDocumentId" &&
                value.value !== null &&
                value.value !== undefined &&
                value.valueLabel
            ) {
                return (
                    <DocumentName
                        fileName={value.valueLabel}
                        icon
                        documentId={String(value.value)}
                    />
                );
            }
            if (entity) {
                return (
                    <div className={styles.entityContainer}>
                        <EntityAvatar entity={entity} size="sm" aria-hidden />
                        <span className={styles.entityName}>{entity.name}</span>
                    </div>
                );
            }
            const isEmpty = value.value === null || value.value === undefined;
            if (isEmpty) {
                return <EmptyValue />;
            }
            if (field === "isDefault") {
                if (typeof value.value === "boolean") {
                    return (
                        <BreakableText
                            text={value.value ? "Yes" : "No"}
                            showTooltip
                        />
                    );
                }
                if (value.value === "true" || value.value === "false") {
                    return (
                        <BreakableText
                            text={value.value === "true" ? "Yes" : "No"}
                            showTooltip
                        />
                    );
                }
            }
            if (field === "type" && typeof value.value === "string") {
                return (
                    <BreakableText
                        text={
                            RULE_GROUP_TYPE_LABELS[value.value] ?? value.value
                        }
                        showTooltip
                    />
                );
            }
            if (field === "lines" && Array.isArray(value.value)) {
                const label = pluralizeWithCount("line", value.value.length);
                return <BreakableText text={label} showTooltip />;
            }
            if (
                field === "order" &&
                orderDirection &&
                (typeof value.value === "number" ||
                    typeof value.value === "string")
            ) {
                return (
                    <span className={styles.orderValue}>
                        <BreakableText text={String(value.value)} showTooltip />
                        <span
                            className={
                                orderDirection === "up"
                                    ? styles.orderUp
                                    : styles.orderDown
                            }
                        >
                            {orderDirection === "up" ? "↑" : "↓"}
                        </span>
                    </span>
                );
            }
            if (
                Array.isArray(value.value) &&
                typeof value.value[0] === "object"
            ) {
                return (
                    <BreakableText
                        text={value.valueLabel ?? `${value.value.length} items`}
                        showTooltip
                    />
                );
            }
            return (
                <BreakableText
                    text={value.valueLabel ?? String(value.value)}
                    showTooltip
                />
            );
        }, [
            value,
            field,
            orderDirection,
            categoryDisplay,
            classDisplay,
            dateDisplay,
            accountDisplay,
            entity,
        ]);

        return (
            <ViewTableCell className={styles.valueCell}>
                {displayValue}
            </ViewTableCell>
        );
    },
);

export const HistoryTransactionsDetailsOldValueCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        HistoryTransactionDetailsRow,
        string
    >) => (
        <ValueCell
            field={row.original.field}
            value={row.original.oldValue}
            resource={row.original.resource}
            entityId={row.original.entityId ?? undefined}
        />
    ),
);

const resolveOrderDirection = (
    oldValue: AuditLogDiff["before"],
    newValue: AuditLogDiff["after"],
): "up" | "down" | null => {
    const before = Number(oldValue.value);
    const after = Number(newValue.value);

    if (
        !Number.isFinite(before) ||
        !Number.isFinite(after) ||
        before === after
    ) {
        return null;
    }

    return after < before ? "up" : "down";
};

export const HistoryTransactionsDetailsNewValueCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        HistoryTransactionDetailsRow,
        string
    >) => {
        const orderDirection =
            row.original.field === "order"
                ? resolveOrderDirection(
                      row.original.oldValue,
                      row.original.newValue,
                  )
                : null;

        return (
            <ValueCell
                field={row.original.field}
                value={row.original.newValue}
                resource={row.original.resource}
                entityId={row.original.entityId ?? undefined}
                orderDirection={orderDirection}
            />
        );
    },
);

HistoryTransactionsDetailsOldValueCell.displayName =
    "HistoryTransactionsDetailsOldValueCell";

HistoryTransactionsDetailsNewValueCell.displayName =
    "HistoryTransactionsDetailsNewValueCell";
